import { useEffect, useState } from 'react';
import authApiClient from '../authApiClient';
import { useUser } from '../contexts/userContext';

/**
 * Hook to update the user's timezone in the backend
 * This will run once after login and update the timezone if needed
 * It uses a localStorage flag to prevent unnecessary updates on every render
 */
export function useTimezoneUpdate() {
  const { user } = useUser();
  const [isUpdated, setIsUpdated] = useState(false);

  useEffect(() => {
    const updateUserTimezone = async () => {
      // Skip if no user ID or already updated
      if (!user?.id) return;
      
      // Check if we've already updated this user's timezone
      const timezoneUpdatedKey = `timezone_updated_${user.id}`;
      let alreadyUpdated = false;
      let currentUserId = null;
      
      try {
        alreadyUpdated = localStorage.getItem(timezoneUpdatedKey);
        currentUserId = localStorage.getItem('current_user_id');
      } catch (error) {
        console.warn('[useTimezoneUpdate] localStorage not available:', error);
      }
      
      if (alreadyUpdated) {
        setIsUpdated(true);
        return;
      }

      const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log(`[useTimezoneUpdate] Detected timezone: ${detectedTimeZone}`);
      
      // Make sure we have a valid user ID that matches the current user
      if (currentUserId && currentUserId !== user.id) {
        console.log(`[useTimezoneUpdate] User ID mismatch, skipping update. Current: ${currentUserId}, User: ${user.id}`);
        return;
      }
      
      try {
        // Use existing authApiClient to maintain auth headers
        console.log(`[useTimezoneUpdate] Updating timezone for user ${user.id}`);
        await authApiClient.put(`/users/${user.id}/account`, {
          timeZone: detectedTimeZone
        });
        
        console.log(`[useTimezoneUpdate] ✅ Updated user time zone to: ${detectedTimeZone}`);
        try {
          localStorage.setItem(timezoneUpdatedKey, 'true');
          localStorage.setItem('current_user_id', user.id);
        } catch (error) {
          console.warn('[useTimezoneUpdate] Failed to save to localStorage:', error);
        }
        setIsUpdated(true);
      } catch (error) {
        console.error('[useTimezoneUpdate] ❌ Failed to update user time zone:', error);
      }
    };
    
    // Only run if we have a logged-in user and haven't updated yet
    if (user?.id && !isUpdated) {
      updateUserTimezone();
    }
  }, [user?.id, isUpdated]);

  return isUpdated;
}

export default useTimezoneUpdate; 